import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatCurrency, formatDate, laneLabel } from "@/lib/format";
import { SERVICE_LANES } from "@/config/resources";
import {
  REVENUE_TYPES, REVENUE_STATUSES, SPLIT_RECIPIENT_ROLES, SPLIT_STATUSES,
  TONE, optOf, labelOf,
} from "@/config/revenueOptions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Plus, Trash2, DollarSign, Split, TrendingUp, Wallet, Hourglass, BadgeDollarSign,
  PieChart, Search, Pencil,
} from "lucide-react";
import { toast } from "sonner";

const Pill = ({ arr, value, testid }) => {
  if (!value) return <span className="text-xs text-muted-foreground">—</span>;
  const o = optOf(arr, value);
  const t = TONE[o?.tone || "gray"];
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border whitespace-nowrap"
      style={{ backgroundColor: t.bg, color: t.color, borderColor: t.border }} data-testid={testid}>
      {o?.label || labelOf(arr, value)}
    </span>
  );
};

const MetricCard = ({ icon: Icon, label, value, accent, testid }) => (
  <Card className="p-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid={testid}>
    <div className="flex items-center gap-3">
      <span className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}1A` }}>
        <Icon size={19} style={{ color: accent }} />
      </span>
      <div className="min-w-0">
        <div className="text-xl font-bold font-display tabular-nums text-[#252221] leading-none">{formatCurrency(value || 0)}</div>
        <div className="text-[11px] text-muted-foreground mt-1 truncate">{label}</div>
      </div>
    </div>
  </Card>
);

const BreakdownCard = ({ title, data, testid }) => {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const max = entries.length ? Math.max(...entries.map((e) => e[1])) : 0;
  return (
    <Card className="p-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-[#252221]"><PieChart size={15} className="text-[#1C4358]" /> {title}</div>
      {entries.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">No realized revenue yet.</p>
      ) : (
        <div className="space-y-2.5">
          {entries.map(([k, v]) => (
            <div key={k}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-[#252221] truncate pr-2">{k}</span>
                <span className="tabular-nums font-semibold text-[#1C4358]">{formatCurrency(v)}</span>
              </div>
              <div className="h-1.5 rounded-full bg-[#EEF3F6] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${max ? (v / max) * 100 : 0}%`, backgroundColor: "#1C4358" }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

const emptyForm = {
  title: "", revenue_type: "home_sale", service_lane: "", case_id: "", partner_id: "",
  assigned_user_id: "", expected_amount: "", actual_amount: "", status: "identified",
  expected_close_date: "", paid_date: "", notes: "", splits: [],
};

const newSplit = () => ({ recipient_role: "mmm_corporate", recipient_name: "MovingMyMama Corporate", percentage: "", flat_amount: "", amount_paid: "", status: "pending", partner_id: "" });

export default function Revenue() {
  const { user, activeBranch } = useAuth();
  const canWrite = Array.isArray(user?.writable_resources) && user.writable_resources.includes("revenue");

  const [items, setItems] = useState([]);
  const [dash, setDash] = useState({});
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [cases, setCases] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ q: "", status: "all", service_lane: "all", revenue_type: "all" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const loadDash = useCallback(() => {
    const params = activeBranch ? { branch_id: activeBranch } : {};
    api.get("/revenue/dashboard", { params }).then((r) => setDash(r.data || {})).catch(() => {});
  }, [activeBranch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (activeBranch) params.branch_id = activeBranch;
      if (filters.q) params.q = filters.q;
      if (filters.status !== "all") params.status = filters.status;
      if (filters.service_lane !== "all") params.service_lane = filters.service_lane;
      if (filters.revenue_type !== "all") params.revenue_type = filters.revenue_type;
      const { data } = await api.get("/revenue", { params });
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, [activeBranch, filters]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { loadDash(); }, [loadDash]);
  useEffect(() => {
    api.get("/partners", { params: { limit: 300 } }).then((r) => setPartners(r.data.items || [])).catch(() => {});
    api.get("/cases", { params: { limit: 300 } }).then((r) => setCases(r.data.items || [])).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data.items || r.data || [])).catch(() => setUsers([]));
  }, []);

  const refresh = () => { load(); loadDash(); };

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1" && canWrite) {
      openNew();
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, canWrite]);

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      ...emptyForm, ...item,
      expected_amount: item.expected_amount ?? item.amount ?? "",
      actual_amount: item.actual_amount ?? "",
      expected_close_date: item.expected_close_date ? String(item.expected_close_date).slice(0, 10) : "",
      paid_date: item.paid_date ? String(item.paid_date).slice(0, 10) : "",
      splits: (item.splits || []).map((s) => ({
        recipient_role: s.recipient_role || "other",
        recipient_name: s.recipient_name || s.label || "",
        percentage: s.percentage ?? s.pct ?? "",
        flat_amount: s.flat_amount ?? "",
        amount_paid: s.amount_paid ?? "",
        status: s.status || (s.paid ? "paid" : "pending"),
        partner_id: s.partner_id || "",
      })),
    });
    setOpen(true);
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const baseAmount = Number(form.actual_amount) || Number(form.expected_amount) || 0;
  const splitCalc = (s) => (s.flat_amount !== "" && s.flat_amount != null ? Number(s.flat_amount) : Math.round(baseAmount * (Number(s.percentage) || 0)) / 100) || 0;

  const addSplit = () => setForm((p) => ({ ...p, splits: [...(p.splits || []), newSplit()] }));
  const updateSplit = (i, k, v) => setForm((p) => {
    const splits = [...p.splits];
    splits[i] = { ...splits[i], [k]: v };
    if (k === "recipient_role" && v !== "partner_vendor") {
      splits[i].recipient_name = labelOf(SPLIT_RECIPIENT_ROLES, v);
      splits[i].partner_id = "";
    }
    if (k === "partner_id" && v) {
      const partner = partners.find((x) => x.id === v);
      if (partner) splits[i].recipient_name = partner.name;
    }
    return { ...p, splits };
  });
  const removeSplit = (i) => setForm((p) => ({ ...p, splits: p.splits.filter((_, idx) => idx !== i) }));
  const totalPct = (form.splits || []).reduce((s, x) => s + (Number(x.percentage) || 0), 0);
  const totalAllocated = (form.splits || []).reduce((s, x) => s + splitCalc(x), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSubmitting(true);
    const payload = {
      ...form,
      expected_amount: form.expected_amount === "" ? null : Number(form.expected_amount),
      actual_amount: form.actual_amount === "" ? null : Number(form.actual_amount),
      splits: (form.splits || []).map((s) => ({
        recipient_role: s.recipient_role,
        recipient_name: s.recipient_name,
        percentage: Number(s.percentage) || 0,
        flat_amount: s.flat_amount === "" ? null : Number(s.flat_amount),
        amount_paid: Number(s.amount_paid) || 0,
        status: s.status,
        partner_id: s.partner_id || null,
      })),
    };
    ["case_id", "partner_id", "assigned_user_id", "service_lane", "expected_close_date", "paid_date", "notes"].forEach((k) => { if (!payload[k]) delete payload[k]; });
    try {
      if (editing) await api.put(`/revenue/${editing.id}`, payload);
      else await api.post("/revenue", payload);
      toast.success(editing ? "Opportunity updated" : "Opportunity created");
      setOpen(false); refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); } finally { setSubmitting(false); }
  };

  const remove = async (item) => {
    try { await api.delete(`/revenue/${item.id}`); toast.success("Removed"); refresh(); } catch { toast.error("Could not remove"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#252221] flex items-center gap-2"><DollarSign size={22} className="text-[#2F7D5A]" /> Revenue & Profit Splits</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Track every opportunity, its outcome, and how the profit is shared.</p>
        </div>
        {canWrite && (
          <Button onClick={openNew} data-testid="new-revenue-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}>
            <Plus size={17} className="mr-1.5" /> New Opportunity
          </Button>
        )}
      </div>

      {/* Revenue dashboard */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4" data-testid="revenue-dashboard">
        <MetricCard icon={TrendingUp} label="Expected revenue" value={dash.expected_revenue} accent="#1C4358" testid="metric-expected" />
        <MetricCard icon={BadgeDollarSign} label="Actual revenue" value={dash.actual_revenue} accent="#2F7D5A" testid="metric-actual" />
        <MetricCard icon={Hourglass} label="Pending (pipeline)" value={dash.pending_revenue} accent="#9A6516" testid="metric-pending" />
        <MetricCard icon={Wallet} label="Paid out" value={dash.paid_revenue} accent="#256B4B" testid="metric-paid" />
        <MetricCard icon={Split} label="Split distribution pending" value={dash.split_distribution_pending} accent="#9C1E22" testid="metric-split-pending" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
        <BreakdownCard title="By service lane" data={dash.by_service_lane} testid="breakdown-lane" />
        <BreakdownCard title="By branch" data={dash.by_branch} testid="breakdown-branch" />
        <BreakdownCard title="By partner" data={dash.by_partner} testid="breakdown-partner" />
        <BreakdownCard title="By team member" data={dash.by_assigned_member} testid="breakdown-member" />
      </div>

      {/* Filters */}
      <Card className="p-3 mb-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        <div className="flex flex-col lg:flex-row lg:items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={filters.q} onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))} placeholder="Search opportunities..." className="pl-9 h-9" data-testid="revenue-search" />
          </div>
          <Select value={filters.status} onValueChange={(v) => setFilters((p) => ({ ...p, status: v }))}>
            <SelectTrigger className="h-9 w-full sm:w-[170px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{REVENUE_STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.service_lane} onValueChange={(v) => setFilters((p) => ({ ...p, service_lane: v }))}>
            <SelectTrigger className="h-9 w-full sm:w-[180px]" data-testid="filter-lane"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All lanes</SelectItem>{SERVICE_LANES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.revenue_type} onValueChange={(v) => setFilters((p) => ({ ...p, revenue_type: v }))}>
            <SelectTrigger className="h-9 w-full sm:w-[190px]" data-testid="filter-type"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-[300px]"><SelectItem value="all">All types</SelectItem>{REVENUE_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Card>

      {/* Opportunities table */}
      <div className="rounded-xl border border-[#E6EDF2] bg-white overflow-hidden" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        {loading ? (
          <div className="p-4 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <div className="mx-auto mb-3 h-14 w-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "rgba(47,125,90,0.12)" }}><DollarSign size={26} className="text-[#2F7D5A]" /></div>
            <p className="font-medium text-[#252221]">No revenue opportunities yet</p>
            <p className="text-sm text-muted-foreground">Track potential revenue and how it's split with partners and your team.</p>
          </div>
        ) : (
          <div className="overflow-x-auto mmm-scroll">
            <Table data-testid="revenue-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Opportunity</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Type</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Expected</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Actual</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Splits</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Status</TableHead>
                  <TableHead className="text-right text-xs font-semibold text-[#1C4358]/80">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} className="hover:bg-[rgba(155,185,206,0.10)] cursor-pointer" onClick={() => canWrite && openEdit(item)} data-testid="revenue-row">
                    <TableCell className="py-3">
                      <div className="font-semibold text-[#252221]">{item.title}</div>
                      <div className="text-xs text-muted-foreground">{item.service_lane ? laneLabel(item.service_lane) : "—"}{item.partner_name ? ` · ${item.partner_name}` : ""}</div>
                    </TableCell>
                    <TableCell className="text-sm">{labelOf(REVENUE_TYPES, item.revenue_type)}</TableCell>
                    <TableCell className="text-sm tabular-nums">{formatCurrency(item.expected_amount ?? item.amount ?? 0)}</TableCell>
                    <TableCell className="text-sm tabular-nums font-medium text-[#2F7D5A]">{item.actual_amount ? formatCurrency(item.actual_amount) : "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{(item.splits || []).length ? `${item.splits.length} parties` : "—"}</TableCell>
                    <TableCell><Pill arr={REVENUE_STATUSES} value={item.status} testid="revenue-status-pill" /></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {canWrite && (
                        <>
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEdit(item); }} data-testid="revenue-edit-button"><Pencil size={14} /></Button>
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); remove(item); }} data-testid="revenue-delete-button"><Trash2 size={15} className="text-[#9C1E22]" /></Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* New / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto mmm-scroll" data-testid="revenue-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2"><DollarSign size={20} className="text-[#2F7D5A]" /> {editing ? "Edit Opportunity" : "New Revenue Opportunity"}</DialogTitle>
            <DialogDescription>Record the deal and configure the profit split per recipient.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="mb-1.5 block text-[13px] font-medium">Title <span className="text-[#9C1E22]">*</span></Label>
                <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="revenue-title" />
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Revenue type</Label>
                <Select value={form.revenue_type} onValueChange={(v) => set("revenue_type", v)}>
                  <SelectTrigger data-testid="revenue-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">{REVENUE_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Service lane</Label>
                <Select value={form.service_lane || "none"} onValueChange={(v) => set("service_lane", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="revenue-lane"><SelectValue placeholder="Select lane" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— None —</SelectItem>{SERVICE_LANES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Expected amount ($)</Label>
                <Input type="number" value={form.expected_amount} onChange={(e) => set("expected_amount", e.target.value)} data-testid="revenue-expected" />
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Actual amount ($)</Label>
                <Input type="number" value={form.actual_amount} onChange={(e) => set("actual_amount", e.target.value)} data-testid="revenue-actual" />
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="revenue-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{REVENUE_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Case</Label>
                <Select value={form.case_id || "none"} onValueChange={(v) => set("case_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="revenue-case"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent className="max-h-[280px]"><SelectItem value="none">— None —</SelectItem>{cases.map((x) => <SelectItem key={x.id} value={x.id}>{x.case_number ? `${x.case_number} · ` : ""}{x.title || x.case_name || "Case"}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Partner involved</Label>
                <Select value={form.partner_id || "none"} onValueChange={(v) => set("partner_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="revenue-partner"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent className="max-h-[280px]"><SelectItem value="none">— None —</SelectItem>{partners.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Assigned team member</Label>
                <Select value={form.assigned_user_id || "none"} onValueChange={(v) => set("assigned_user_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="revenue-assignee"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent className="max-h-[260px]"><SelectItem value="none">— None —</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Expected close</Label>
                <Input type="date" value={form.expected_close_date} onChange={(e) => set("expected_close_date", e.target.value)} data-testid="revenue-close-date" />
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Paid date</Label>
                <Input type="date" value={form.paid_date} onChange={(e) => set("paid_date", e.target.value)} data-testid="revenue-paid-date" />
              </div>
            </div>

            {/* Profit split builder */}
            <div className="rounded-xl border border-[#E6EDF2] p-3.5 bg-[#F7FAFC]">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 font-semibold text-[#252221] text-sm"><Split size={16} className="text-[#1C4358]" /> Profit split</div>
                <Button type="button" size="sm" variant="outline" onClick={addSplit} data-testid="add-split-button"><Plus size={14} className="mr-1" /> Add recipient</Button>
              </div>
              {(form.splits || []).length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">No recipients yet. Add corporate, branch, team members, or partners to allocate this deal.</p>
              ) : (
                <div className="space-y-2.5">
                  {form.splits.map((s, i) => (
                    <div key={i} className="rounded-lg border border-[#E6EDF2] bg-white p-2.5" data-testid="split-row">
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-12 sm:col-span-3">
                          <Label className="mb-1 block text-[11px] text-muted-foreground">Recipient role</Label>
                          <Select value={s.recipient_role} onValueChange={(v) => updateSplit(i, "recipient_role", v)}>
                            <SelectTrigger className="h-9" data-testid="split-role"><SelectValue /></SelectTrigger>
                            <SelectContent>{SPLIT_RECIPIENT_ROLES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-12 sm:col-span-3">
                          <Label className="mb-1 block text-[11px] text-muted-foreground">Recipient name</Label>
                          {s.recipient_role === "partner_vendor" ? (
                            <Select value={s.partner_id || ""} onValueChange={(v) => updateSplit(i, "partner_id", v)}>
                              <SelectTrigger className="h-9" data-testid="split-partner"><SelectValue placeholder="Choose partner" /></SelectTrigger>
                              <SelectContent>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : (
                            <Input className="h-9" value={s.recipient_name} onChange={(e) => updateSplit(i, "recipient_name", e.target.value)} data-testid="split-name" />
                          )}
                        </div>
                        <div className="col-span-4 sm:col-span-2">
                          <Label className="mb-1 block text-[11px] text-muted-foreground">%</Label>
                          <Input className="h-9" type="number" value={s.percentage} onChange={(e) => updateSplit(i, "percentage", e.target.value)} placeholder="0" data-testid="split-pct" disabled={s.flat_amount !== "" && s.flat_amount != null} />
                        </div>
                        <div className="col-span-4 sm:col-span-2">
                          <Label className="mb-1 block text-[11px] text-muted-foreground">Flat ($)</Label>
                          <Input className="h-9" type="number" value={s.flat_amount} onChange={(e) => updateSplit(i, "flat_amount", e.target.value)} placeholder="—" data-testid="split-flat" />
                        </div>
                        <div className="col-span-4 sm:col-span-2 flex items-end justify-between gap-1">
                          <div>
                            <Label className="mb-1 block text-[11px] text-muted-foreground">Calc.</Label>
                            <div className="h-9 flex items-center text-sm font-semibold tabular-nums text-[#1C4358]">{formatCurrency(splitCalc(s))}</div>
                          </div>
                          <button type="button" onClick={() => removeSplit(i)} className="text-[#9C1E22] p-1.5 mb-0.5" data-testid="split-remove"><Trash2 size={15} /></button>
                        </div>
                      </div>
                      <div className="grid grid-cols-12 gap-2 items-end mt-2">
                        <div className="col-span-6 sm:col-span-3">
                          <Label className="mb-1 block text-[11px] text-muted-foreground">Amount paid ($)</Label>
                          <Input className="h-9" type="number" value={s.amount_paid} onChange={(e) => updateSplit(i, "amount_paid", e.target.value)} placeholder="0" data-testid="split-paid-amount" />
                        </div>
                        <div className="col-span-6 sm:col-span-3">
                          <Label className="mb-1 block text-[11px] text-muted-foreground">Status</Label>
                          <Select value={s.status} onValueChange={(v) => updateSplit(i, "status", v)}>
                            <SelectTrigger className="h-9" data-testid="split-status"><SelectValue /></SelectTrigger>
                            <SelectContent>{SPLIT_STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between text-xs font-medium pt-1">
                    <span className={totalPct === 100 ? "text-[#2F7D5A]" : totalPct > 100 ? "text-[#9C1E22]" : "text-muted-foreground"}>
                      Total allocated: {totalPct}% {totalPct !== 100 && totalPct > 0 && `(${100 - totalPct}% remaining)`}
                    </span>
                    <span className="text-[#1C4358] tabular-nums">{formatCurrency(totalAllocated)} of {formatCurrency(baseAmount)}</span>
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label className="mb-1.5 block text-[13px] font-medium">Notes</Label>
              <Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="revenue-notes" />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="revenue-submit-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}>{submitting ? "Saving..." : "Save"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
